import React from "react";
import TextField, { TextFieldProps } from "@mui/material/TextField";

/**
 * CompactTableTextField
 *
 * A reusable, ultra-compact TextField for use in tables and custom viewers.
 * - Flexible width that adapts to container size with text overflow handling.
 * - Minimal height, font size, and padding.
 * - Never expands table row height.
 * - Accepts all standard TextField props.
 * - Use everywhere in transformed custom views for editable table cells.
 *
 * Example usage:
 * <CompactTableTextField value={...} onChange={...} />
 */
const CompactTableTextField = React.forwardRef<
  HTMLInputElement,
  TextFieldProps
>((props, ref) => (
  <TextField
    {...props}
    inputRef={ref}
    size="small"
    variant={props.variant || "outlined"}
    sx={{
      width: "100%",
      minWidth: 40,
      maxWidth: "100%",
      m: 0,
      p: 0,
      fontSize: "0.75rem",
      lineHeight: 1.2,
      "& .MuiInputBase-root": {
        m: 0,
        p: 0,
        height: 28,
        minHeight: 24,
      },
      "& .MuiInputBase-input": {
        fontSize: "0.75rem",
        p: "2px 4px",
        height: 20,
        minHeight: 16,
        textAlign: "center",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      ...props.sx,
    }}
    inputProps={{
      ...props.inputProps,
      style: {
        ...(props.inputProps?.style || {}),
        textAlign: "center",
        padding: 0,
        fontSize: "0.75rem",
        height: 20,
        minHeight: 16,
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    }}
  />
));

export default CompactTableTextField;
